import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import supabase from "../../../src/supabaseClient";
import InactiveShipment from "../../components/InactiveShipment";
import SelectedCard from "../../components/SelectedCard";
import MetricCard from "../../components/MetricCard";
import UnbilledReminderModal from "../../components/dashboard/UnbilledReminderModal";
import BillingNotificationsBell from "../../components/dashboard/BillingNotificationsBell";
import InactiveShipmentButton from "../../components/dashboard/InactiveShipmentButton";
import api, { API_BASE_URL } from "../../config/api";
import {
  buildDashboardQueryParams,
  DASHBOARD_MONTH_OPTIONS,
} from "../../utils/dashboardDateFilter";

const formatReminderDate = (value) => {
  if (!value) return "No date";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "No date" : parsed.toLocaleDateString();
};

const ReminderBoard = ({ reminders, onViewAllUnliquidated, onViewAllUnbilled }) => (
  <div className="bg-white/90 backdrop-blur-sm rounded-2xl border border-amber-100 p-5 shadow-lg">
    <div>
      <p className="text-xs font-semibold uppercase tracking-widest text-amber-700">
        Bulletin Board
      </p>
      <h2 className="text-2xl font-bold text-blue">Pending Reminders</h2>
      <p className="text-sm text-gray-600">
        Unbilled shipments and requests that are still not fully liquidated.
      </p>
    </div>

    <div className="mt-5 grid grid-cols-1 gap-4 xl:grid-cols-2">
      {[
        {
          key: "unbilled",
          title: "Unbilled Shipments",
          tone: "amber",
          empty: "No unbilled shipments found.",
        },
        {
          key: "unliquidated",
          title: "Unliquidated Requests",
          tone: "rose",
          empty: "No unliquidated requests found.",
        },
      ].map((section) => {
        const items = (reminders?.[section.key]?.items || []).slice(0, 6);
        const count = reminders?.[section.key]?.count || 0;
        const toneClass =
          section.tone === "rose"
            ? "border-rose-100 bg-rose-50 text-rose-700"
            : "border-amber-100 bg-amber-50 text-amber-700";

        return (
          <div key={section.key} className={`rounded-2xl border px-4 py-4 ${toneClass}`}>
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-lg font-semibold">{section.title}</h3>
              <div className="flex items-center gap-2">
                {section.key === "unliquidated" ? (
                  <button
                    type="button"
                    onClick={onViewAllUnliquidated}
                    className="rounded-full border border-white/80 bg-white/80 px-3 py-1 text-xs font-semibold text-slate-700 transition hover:bg-white"
                  >
                    View all
                  </button>
                ) : null}
                {section.key === "unbilled" ? (
                  <button
                    type="button"
                    onClick={onViewAllUnbilled}
                    className="rounded-full border border-white/80 bg-white/80 px-3 py-1 text-xs font-semibold text-slate-700 transition hover:bg-white"
                  >
                    View all
                  </button>
                ) : null}
                <span className="rounded-full bg-white/80 px-3 py-1 text-sm font-bold text-slate-800">
                  {count}
                </span>
              </div>
            </div>

            <div className="mt-4 space-y-3">
              {items.length ? (
                items.map((item) => (
                  <div key={`${section.key}-${item.id}`} className="rounded-xl border border-white/80 bg-white/80 px-3 py-3 text-sm shadow-sm">
                    <div className="font-semibold text-slate-900">{item.title}</div>
                    <div className="text-slate-600">{item.subtitle}</div>
                    <div className="mt-2 flex items-center justify-between gap-2 text-xs text-slate-500">
                      <span className="uppercase tracking-wide">{item.meta}</span>
                      <span>{formatReminderDate(item.date)}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="rounded-xl border border-dashed border-white/80 bg-white/60 px-3 py-6 text-center text-sm text-slate-500">
                  {section.empty}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

const SuperDashboard = () => {
  const user = useSelector((state) => state.auth.user);
  const navigate = useNavigate();
  const currentYear = String(new Date().getFullYear());
  const yearOptions = Array.from({ length: 10 }, (_, i) =>
    String(new Date().getFullYear() - i)
  );
  const monthOptions = DASHBOARD_MONTH_OPTIONS;
  const [totalShipment, setTotalShipment] = useState("");
  const [upcomingShipments, setUpcomingShipments] = useState(null);
  const [activeShipments, setActiveShipments] = useState(null);
  const [activeEmployees, setActiveEmployees] = useState(null);
  const [notifications, setNotifications] = useState(null);
  const [completedShipments, setCompletedShipments] = useState(null);
  const [billedShipmentsCount, setBilledShipmentsCount] = useState(0);
  const [cancelledShipmentsCount, setCancelledShipmentsCount] = useState(0);
  const [unbilledModalOpen, setUnbilledModalOpen] = useState(false);
  const [reminders, setReminders] = useState({
    unbilled: { count: 0, items: [] },
    unliquidated: { count: 0, items: [] },
  });
  const [selectedCard, setSelectedCard] = useState("active");
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [selectedShipmentType, setSelectedShipmentType] = useState("all");
  const [metricsLoading, setMetricsLoading] = useState(false);
  const shipmentTypeOptions = ["AIR", "SEA", "LAND", "DOMESTIC", "BROKERAGE"];

  const applySelectedDateToShipmentQuery = (query) => {
    if (selectedShipmentType !== "all") {
      query = query.eq("shipment_type", selectedShipmentType);
    }
    if (selectedYear === "all") {
      return query;
    }

    if (selectedMonth !== "all") {
      const nextYear = selectedMonth === "12" ? Number(selectedYear) + 1 : Number(selectedYear);
      const nextMonth = selectedMonth === "12" ? "01" : String(Number(selectedMonth) + 1).padStart(2, "0");

      return query
        .gte("date", `${selectedYear}-${selectedMonth}-01`)
        .lt("date", `${nextYear}-${nextMonth}-01`);
    }

    return query
      .gte("date", `${selectedYear}-01-01`)
      .lt("date", `${Number(selectedYear) + 1}-01-01`);
  };

  useEffect(() => {
    if (selectedYear === "all" && selectedMonth !== "all") {
      setSelectedMonth("all");
    }
  }, [selectedMonth, selectedYear, selectedShipmentType]);

  // Consolidated initial data fetch - no more duplicate calls
  useEffect(() => {
    const fetchAllData = async () => {
      setMetricsLoading(true);
      try {
        // Fetch all data in parallel for better performance
        await Promise.all([
          fetchOverallTotalShipment(),
          fetchActiveShipments(),
          fetchActiveEmployees(),
          fetchUpcomingShipments(),
          fetchNotifications(),
          fetchCompletedShipments(),
          fetchBilledShipments(),
          fetchCancelledShipments(),
          fetchReminders(),
        ]);
      } catch (error) {
        console.error("Error fetching initial data:", error);
      } finally {
        setMetricsLoading(false);
      }
    };

    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, selectedYear, selectedShipmentType]);

  const fetchOverallTotalShipment = async () => {
    try {
      const filterParams = buildDashboardQueryParams({
      year: selectedYear,
      month: selectedMonth,
      shipmentType: selectedShipmentType,
      });
      const { data } = await api.get(
        `${API_BASE_URL}/api/superadmin/overall-shipments${filterParams}`
      );
      setTotalShipment(data.overallTotalShipments);
    } catch (error) {
      console.error("Error fetching total shipments:", error);
    }
  };
  const fetchActiveShipments = async () => {
    try {
      const filterParams = buildDashboardQueryParams({
        year: selectedYear,
        month: selectedMonth,
        shipmentType: selectedShipmentType,
      });
      const { data } = await api.get(
        `${API_BASE_URL}/api/superadmin/active-shipments${filterParams}`
      );
      setActiveShipments(data.activeShipments);
      // console.log("Fetch active shipments: ", data);
    } catch (error) {
      console.log("Error fetching active shipments:", error);
    }
  };

  const fetchUpcomingShipments = async () => {
    try {
      const filterParams = buildDashboardQueryParams({
        year: selectedYear,
        month: selectedMonth,
        shipmentType: selectedShipmentType,
      });
      const { data } = await api.get(
        `${API_BASE_URL}/api/superadmin/upcoming-shipments${filterParams}`
      );
      setUpcomingShipments(data.upcomingShipments);
    } catch (error) {
      console.log("Error fetching upcoming shipments:", error);
    }
  };

  const fetchCompletedShipments = async () => {
    try {
      const filterParams = buildDashboardQueryParams({
        year: selectedYear,
        month: selectedMonth,
        shipmentType: selectedShipmentType,
      });
      const { data } = await api.get(
        `${API_BASE_URL}/api/superadmin/completed-shipments${filterParams}`
      );
      setCompletedShipments(data.completedShipments);
    } catch (error) {
      console.log("Error fetching completed shipments:", error);
    }
  };

  const fetchBilledShipments = async () => {
    try {
      const billedShipmentsQuery = applySelectedDateToShipmentQuery(
        supabase
          .from("shipments")
          .select("*", { count: "exact", head: true })
          .eq("is_billed", true),
        selectedShipmentType
      );

      const { count, error } = await billedShipmentsQuery;

      if (!error) {
        setBilledShipmentsCount(count || 0);
      }
    } catch (error) {
      console.log("Error fetching billed shipments:", error);
    }
  };

  const fetchCancelledShipments = async () => {
    try {
      const cancelledShipmentsQuery = applySelectedDateToShipmentQuery(
        supabase
          .from("shipments")
          .select("*", { count: "exact", head: true })
          .eq("status", "Cancelled"),
        selectedShipmentType
      );

      const { count, error } = await cancelledShipmentsQuery;

      if (!error) {
        setCancelledShipmentsCount(count || 0);
      }
    } catch (error) {
      console.log("Error fetching cancelled shipments:", error);
    }
  };

  const fetchReminders = async () => {
    try {
      const filterParams = buildDashboardQueryParams({
        year: selectedYear,
        month: selectedMonth,
        shipmentType: selectedShipmentType,
      });
      const { data } = await api.get(`${API_BASE_URL}/api/superadmin/reminders${filterParams}`);
      if (data?.success) {
        setReminders(
          data.reminders || {
            unbilled: { count: 0, items: [] },
            unliquidated: { count: 0, items: [] },
          }
        );
      }
    } catch (error) {
      console.log("Error fetching reminders:", error);
    }
  };

  // Add real-time subscription for shipments to replace polling
  useEffect(() => {
    const subscription = supabase
      .channel("super-dashboard-shipments")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "shipments" },
        async () => {
          // Refetch shipment counts when any shipment changes
          await Promise.all([
            fetchOverallTotalShipment(),
            fetchActiveShipments(),
            fetchUpcomingShipments(),
            fetchCompletedShipments(),
            fetchBilledShipments(),
            fetchCancelledShipments(),
            fetchReminders(),
          ]);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, selectedYear, selectedShipmentType]);

  const fetchActiveEmployees = async () => {
    try {
      const { data } = await api.get(
        "https://track-trace-server-production.up.railway.app/api/superadmin/active-employees"
      );
      setActiveEmployees(data.activeEmployees);
    } catch (error) {
      console.log("Error fetching active employees:", error);
    }
  };

  useEffect(() => {
    fetchNotifications(); // Initial fetch of notifications

    // Set up a timer to reset notifications every 24 hours (86,400,000 ms)
    const interval = setInterval(() => {
      fetchNotifications(); // Re-fetch notifications every 24 hours
    }, 86400000);

    // Cleanup interval on component unmount
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    const { data, error } = await supabase
      .from("notification")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching notification:", error);
    } else {
      // Filter notifications to only include those within the past 24 hours
      const now = new Date();
      const recentNotifications = data.filter((notification) => {
        const notificationDate = new Date(notification.created_at);
        const isRecent = now - notificationDate <= 86400000;
        if (!isRecent) return false;

        if (notification.type === "billing upload") {
          return String(notification.user_id || "") === String(user?.user_id || user?.id || "");
        }

        return true;
      });

      setNotifications(recentNotifications);
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const notificationDate = new Date(timestamp);
    const timeDifference = Math.abs(now - notificationDate);

    const days = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (timeDifference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const minutes = Math.floor(
      (timeDifference % (1000 * 60 * 60)) / (1000 * 60)
    );

    if (days >= 1) {
      return days === 1 ? "1 day ago" : `${days} days ago`;
    }

    if (hours >= 1) {
      return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
    }

    return minutes === 1 ? "1 minute ago" : `${minutes} minutes ago`;
  };

  return (
    <>
      <section
        id="dashboard"
        className="flex flex-col justify-center font-poppins min-h-screen"
      >
        <div className="flex flex-col bg-gray-100 justify-center p-[5px] gap-[30px]">
          <div className="flex flex-col gap-4 rounded-2xl border border-gray-100 bg-white px-5 py-5 shadow-lg sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-500">
                Dashboard Overview
              </p>
              <h1 className="text-2xl font-bold text-blue sm:text-3xl">
                Super Admin Dashboard
              </h1>
            </div>
            <div className="w-full sm:w-auto">
              <label
                htmlFor="super-dashboard-year-filter"
                className="mb-2 block text-sm font-semibold text-gray-700"
              >
                Filter by Period
              </label>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="relative">
                  <select
                    id="super-dashboard-year-filter"
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                    className="w-full appearance-none rounded-xl border border-gray-300 bg-white py-3 pl-4 pr-11 text-blue font-semibold shadow-sm focus:outline-none focus:ring-2 focus:ring-blue/30 sm:min-w-[220px]"
                  >
                    <option value="all">All Years</option>
                    {yearOptions.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-blue">
                    <i className="fas fa-chevron-down text-sm" />
                  </div>
                </div>
                <div className="relative">
                  <select
                    id="super-dashboard-shipment-type-filter"
                    value={selectedShipmentType}
                    onChange={(e) => setSelectedShipmentType(e.target.value)}
                    className="w-full appearance-none rounded-xl border border-gray-300 bg-white py-3 pl-4 pr-11 text-blue font-semibold shadow-sm focus:outline-none focus:ring-2 focus:ring-blue/30 sm:min-w-[220px]"
                  >
                    <option value="all">All Shipment Types</option>
                    {shipmentTypeOptions.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-blue">
                    <i className="fas fa-chevron-down text-sm" />
                  </div>
                </div>
                <div className="relative">
                  <select
                    id="super-dashboard-month-filter"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    disabled={selectedYear === "all"}
                    className="w-full appearance-none rounded-xl border border-gray-300 bg-white py-3 pl-4 pr-11 text-blue font-semibold shadow-sm focus:outline-none focus:ring-2 focus:ring-blue/30 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400 sm:min-w-[220px]"
                  >
                    {monthOptions.map((month) => (
                      <option key={month.value} value={month.value}>
                        {month.label}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-blue">
                    <i className="fas fa-chevron-down text-sm" />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <InactiveShipmentButton
                    user={user}
                    shipmentsPath="/superAdmin/shipment-management"
                  />
                  <BillingNotificationsBell
                    user={user}
                    billingListPath="/superAdmin/billing-list"
                  />
                </div>
              </div>
            </div>
          </div>

          {useMemo(
            () => (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-[40px] md:gap-[20px] w-full">
                <MetricCard
                  icon="fas fa-shipping-fast"
                  count={metricsLoading ? <i className="fas fa-spinner fa-spin" /> : activeShipments || "0"}
                  title="Active Shipments"
                  cardType="active"
                  selectedCard={selectedCard}
                  onClick={() => setSelectedCard("active")}
                />
                <MetricCard
                  icon="fa-solid fa-hourglass-half"
                  count={metricsLoading ? <i className="fas fa-spinner fa-spin" /> : upcomingShipments || "0"}
                  title="Shipment Nomination"
                  cardType="upcoming"
                  selectedCard={selectedCard}
                  onClick={() => setSelectedCard("upcoming")}
                />
                <MetricCard
                  icon="fa-solid fa-chart-simple"
                  count={metricsLoading ? <i className="fas fa-spinner fa-spin" /> : totalShipment || "0"}
                  title="Total Shipments"
                  cardType="total"
                  selectedCard={selectedCard}
                  onClick={() => setSelectedCard("total")}
                />
                <MetricCard
                  icon="fas fa-circle-check"
                  count={metricsLoading ? <i className="fas fa-spinner fa-spin" /> : completedShipments || "0"}
                  title="Delivered Shipments"
                  cardType="completed"
                  selectedCard={selectedCard}
                  onClick={() => setSelectedCard("completed")}
                />
                <MetricCard
                  icon="fas fa-ban"
                  count={metricsLoading ? <i className="fas fa-spinner fa-spin" /> : cancelledShipmentsCount || "0"}
                  title="Cancelled Shipments"
                  cardType="cancelled"
                  selectedCard={selectedCard}
                  onClick={() => setSelectedCard("cancelled")}
                />
                <MetricCard
                  icon="fas fa-receipt"
                  count={metricsLoading ? <i className="fas fa-spinner fa-spin" /> : `${billedShipmentsCount || "0"} / ${totalShipment || "0"}`}
                  title="Billed Shipments"
                  cardType="billed"
                  selectedCard={selectedCard}
                  onClick={() => setSelectedCard("billed")}
                />
              </div>
            ),
            [
              selectedCard,
              activeShipments,
              upcomingShipments,
              totalShipment,
              completedShipments,
              cancelledShipmentsCount,
              billedShipmentsCount,
              metricsLoading,
            ]
          )}

          <ReminderBoard
            reminders={reminders}
            onViewAllUnliquidated={() => navigate("/superAdmin/liquidation")}
            onViewAllUnbilled={() => setUnbilledModalOpen(true)}
          />

          <UnbilledReminderModal
            isOpen={unbilledModalOpen}
            onClose={() => setUnbilledModalOpen(false)}
            shipments={reminders?.unbilled?.items || []}
            user={user}
            onUploaded={() => {
              setUnbilledModalOpen(false);
              fetchBilledShipments();
              fetchReminders();
            }}
          />

          {/* Selected Card Component */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            {useMemo(
              () => (
                <SelectedCard
                  selectedCard={selectedCard}
                  selectedYear={selectedYear}
                  selectedMonth={selectedMonth}
                  selectedShipmentType={selectedShipmentType}
                />
              ),
              [selectedCard, selectedMonth, selectedYear, selectedShipmentType]
            )}
          </div>

          {useMemo(
            () => (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full">
                {/* Active Employees */}
                <div className="bg-white/90 backdrop-blur-sm w-full shadow-lg p-4 sm:p-5 md:p-6 rounded-2xl max-h-[400px] sm:max-h-[450px] overflow-hidden border border-gray-100">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0 mb-4 sm:mb-6">
                    <h2 className="text-xl sm:text-2xl font-bold text-blue">
                      Active Employees
                    </h2>
                    <div className="flex items-center gap-2 px-3 py-1 bg-blue/10 text-blue rounded-full text-sm font-medium w-fit">
                      <i className="fas fa-users text-blue text-sm"></i>
                      <span>{activeEmployees?.length || 0} Online</span>
                    </div>
                  </div>

                  <div className="space-y-3 sm:space-y-4 max-h-[280px] sm:max-h-[320px] overflow-y-auto pr-1 sm:pr-2 scrollbar-thin scrollbar-thumb-blue/30 scrollbar-track-transparent">
                    {activeEmployees && activeEmployees.length > 0 ? (
                      activeEmployees.map((employee, index) => (
                        <div
                          key={index}
                          className="bg-gradient-to-r from-blue/5 to-blue/10 shadow-sm p-3 sm:p-4 rounded-xl flex items-center space-x-3 sm:space-x-4 border border-blue/10"
                        >
                          <div className="relative flex-shrink-0">
                            <div className="w-10 sm:w-12 h-10 sm:h-12 bg-blue to-blue/80 rounded-full flex items-center justify-center shadow-md">
                              <i className="fas fa-user text-white text-sm sm:text-lg"></i>
                            </div>
                            <div className="absolute -bottom-1 -right-1 w-4 sm:w-5 h-4 sm:h-5 bg-yellow rounded-full border-2 border-white shadow-lg">
                              <div className="w-full h-full bg-yellow rounded-full animate-ping"></div>
                              <div className="absolute inset-0 w-full h-full bg-yellow rounded-full animate-pulse"></div>
                            </div>
                          </div>
                          <div className="flex-1 text-left min-w-0">
                            <h3 className="text-blue font-semibold text-sm sm:text-base md:text-lg truncate">
                              {employee.user_name}
                            </h3>
                            <p className="text-grey text-xs sm:text-sm font-medium">
                              {employee.department}
                            </p>
                          </div>
                          <div className="flex flex-col items-center gap-1 flex-shrink-0">
                            <div className="bg-blue/20 text-blue px-3 sm:px-4 py-1 sm:py-2 rounded-full text-xs sm:text-sm font-bold">
                              {employee.shipments?.length || 0}
                            </div>
                            <span className="text-xs text-grey font-medium">
                              shipments
                            </span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center text-grey py-8 sm:py-12">
                        <i className="fas fa-users text-3xl sm:text-4xl mb-3 sm:mb-4 opacity-30"></i>
                        <p className="font-medium text-sm sm:text-base">
                          No active employees
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Notifications */}
                <div className="bg-white/90 backdrop-blur-sm w-full shadow-lg p-4 sm:p-5 md:p-6 rounded-2xl max-h-[400px] sm:max-h-[450px] overflow-hidden border border-gray-100">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0 mb-4 sm:mb-6">
                    <h2 className="text-xl sm:text-2xl font-bold text-blue">
                      Notifications
                    </h2>
                    <div className="flex items-center gap-2 px-3 py-1 bg-blue/10 text-blue rounded-full text-sm font-medium w-fit">
                      <i className="fas fa-bell text-xs"></i>
                      {notifications?.length || 0} Today
                    </div>
                  </div>

                  <div className="space-y-3 sm:space-y-4 max-h-[280px] sm:max-h-[320px] overflow-y-auto pr-1 sm:pr-2 scrollbar-thin scrollbar-thumb-blue/30 scrollbar-track-transparent">
                    {notifications && notifications.length > 0 ? (
                      notifications
                        .sort(
                          (a, b) =>
                            new Date(b.created_at) - new Date(a.created_at)
                        )
                        .map((notification, index) => {
                          const isMostRecent = index === 0;

                          return (
                            <div
                              key={index}
                              className={`relative flex items-start gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl ${
                                isMostRecent
                                  ? "bg-blue text-white shadow-lg border border-blue"
                                  : "bg-gradient-to-r from-gray-50 to-blue-50/30 border border-gray-100"
                              }`}
                            >
                              {isMostRecent && (
                                <div className="absolute top-1 right-1 w-3 sm:w-4 h-3 sm:h-4 bg-yellow rounded-full shadow-lg z-10">
                                  <div className="w-3 sm:w-4 h-3 sm:h-4 bg-yellow rounded-full animate-ping absolute"></div>
                                  <div className="w-2 sm:w-3 h-2 sm:h-3 bg-yellow rounded-full animate-pulse absolute inset-0.5"></div>
                                </div>
                              )}

                              <div
                                className={`flex-shrink-0 w-10 sm:w-12 h-10 sm:h-12 rounded-full flex items-center justify-center ${
                                  isMostRecent ? "bg-white/20" : "bg-blue/10"
                                }`}
                              >
                                <i
                                  className={`fas text-sm sm:text-lg ${
                                    notification.type === "shipment"
                                      ? "fa-shipping-fast"
                                      : notification.type === "user add"
                                      ? "fa-user-plus"
                                      : notification.type === "user minus"
                                      ? "fa-user-minus"
                                      : notification.type === "edit password"
                                      ? "fa-user-pen"
                                      : "fa-bell"
                                  } ${
                                    isMostRecent ? "text-white" : "text-blue"
                                  }`}
                                ></i>
                              </div>

                              <div className="flex-1 min-w-0 text-left">
                                <div className="mb-2">
                                  <h3
                                    className={`font-semibold text-sm sm:text-base ${
                                      isMostRecent ? "text-white" : "text-blue"
                                    }`}
                                  >
                                    {notification.message}
                                  </h3>
                                </div>
                                {/* Mobile: Stacked layout */}
                                <div className="flex flex-col space-y-1 sm:hidden">
                                  <div
                                    className={`text-xs font-medium ${
                                      isMostRecent
                                        ? "text-blue-100"
                                        : "text-grey"
                                    }`}
                                  >
                                    by {notification.user_name}
                                  </div>
                                  <div
                                    className={`text-xs ${
                                      isMostRecent
                                        ? "text-blue-200"
                                        : "text-grey/70"
                                    }`}
                                  >
                                    {formatTimeAgo(notification.created_at)}
                                  </div>
                                </div>
                                {/* Desktop: Inline layout */}
                                <div className="hidden sm:flex items-center gap-2 text-xs">
                                  <span
                                    className={`font-medium ${
                                      isMostRecent
                                        ? "text-blue-100"
                                        : "text-grey"
                                    }`}
                                  >
                                    by {notification.user_name}
                                  </span>
                                  <span
                                    className={`${
                                      isMostRecent
                                        ? "text-blue-200"
                                        : "text-grey/70"
                                    }`}
                                  >
                                    • {formatTimeAgo(notification.created_at)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })
                    ) : (
                      <div className="text-center text-grey py-8 sm:py-12">
                        <i className="fas fa-bell text-3xl sm:text-4xl mb-3 sm:mb-4 opacity-30"></i>
                        <p className="font-medium text-sm sm:text-base">
                          No notifications available
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ),
            [activeEmployees, notifications]
          )}

          {/* Inactive Status Alert */}
          <div className="grid grid-cols-1 w-full">
            <div className="bg-white/90 backdrop-blur-sm p-6 sm:p-8 shadow-lg w-full rounded-2xl border border-gray-100">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-blue">
                  Inactive Shipment Tracker
                </h2>
                <div className="flex items-center gap-2 px-4 py-2 bg-yellow/10 text-blue rounded-full text-sm font-medium border border-yellow/30">
                  <i className="fas fa-exclamation-triangle text-yellow"></i>
                  Attention Required
                </div>
              </div>
              <div className="">
                <InactiveShipment
                  selectedYear={selectedYear}
                  selectedMonth={selectedMonth}
                  selectedShipmentType={selectedShipmentType}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default SuperDashboard;
